import { Link } from "react-router-dom";
import { useLocalePath, useT } from "../../i18n";
import { fmt } from "../../lib/format";
import { VehicleCard, VehicleCardSkeleton } from "../VehicleCard";

const GRID = "grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 min-[1680px]:grid-cols-4";
const SIZES = "(min-width: 1680px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";

const Message = ({ testId, label, title, body, action }) => (
  <div data-testid={testId} className="border-y border-ink/15 py-20 text-center sm:py-28">
    <p className="micro-label text-ink/45">{label}</p>
    <h2 className="font-display mx-auto mt-4 max-w-lg text-3xl leading-tight [text-wrap:balance] [word-break:auto-phrase] sm:text-4xl">{title}</h2>
    <p className="mx-auto mt-4 max-w-md text-sm text-ink/60 [word-break:auto-phrase]">{body}</p>
    <div className="mt-10">{action}</div>
  </div>
);

export const InventoryResults = ({ entry, status, loadingMore, loadMore, retry, onClear, noStock }) => {
  const t = useT();
  const lp = useLocalePath();
  if (status === "error" && !entry?.items?.length) {
    return (
      <Message testId="inventory-error" label="—" title={t("inventory.errorTitle")} body={t("inventory.errorBody")}
        action={<button type="button" onClick={retry} className="btn btn-dark" data-testid="inventory-retry">{t("inventory.retry")}</button>} />
    );
  }
  if (status === "loading" && !entry) {
    return (
      <div className={GRID} aria-busy="true" data-testid="inventory-loading">
        {Array.from({ length: 6 }, (_, i) => <VehicleCardSkeleton key={i} tone="light" />)}
      </div>
    );
  }
  if (noStock) {
    return (
      <Message testId="inventory-coming-soon" label={t("inventory.soonLabel")} title={t("inventory.soonTitle")} body={t("inventory.soonBody")}
        action={
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={lp("sourcing")} className="btn btn-dark" data-testid="inventory-soon-sourcing">{t("inventory.soonSourcing")}</Link>
            <Link to={lp("contact")} className="btn btn-ghost-dark" data-testid="inventory-soon-contact">{t("ui.contactUs")}</Link>
          </div>
        } />
    );
  }
  if (!entry?.items?.length) {
    return (
      <Message testId="inventory-empty" label={t("inventory.emptyLabel")} title={t("inventory.emptyTitle")} body={t("inventory.emptyBody")}
        action={<button type="button" onClick={onClear} className="btn btn-dark" data-testid="inventory-clear-filters">{t("inventory.clearFilters")}</button>} />
    );
  }
  const hasMore = entry.items.length < entry.total;
  return (
    <div className={status === "loading" ? "opacity-50 transition-opacity duration-300" : "transition-opacity duration-300"}>
      <div className={GRID} data-testid="inventory-grid">
        {entry.items.map((v, i) => (
          <VehicleCard key={v.id} vehicle={v} tone="light" sizes={SIZES} maxVariant="medium" eager={i < 3} />
        ))}
      </div>
      <div className="mt-20 flex flex-col items-center gap-6">
        <p className="micro-label tabular-nums text-ink/50" data-testid="inventory-showing">
          {fmt(t("inventory.showing"), { shown: entry.items.length, total: entry.total })}
        </p>
        <div className="h-px w-40 bg-ink/10">
          <div className="h-px bg-ink transition-[width] duration-500" style={{ width: `${(entry.items.length / entry.total) * 100}%` }} />
        </div>
        {hasMore && (
          <button type="button" onClick={loadMore} disabled={loadingMore} className="btn btn-ghost-dark min-w-[14rem]" data-testid="inventory-load-more">
            {loadingMore ? "…" : t("inventory.loadMore")}
          </button>
        )}
      </div>
    </div>
  );
};
